import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { isAuthReady } from '@/services/supabase';

export interface AuthGuardState {
  user: { id: string } | null;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Auth Guard Hook - Ensures authentication state is fully stabilized
 * before allowing data operations. Prevents race conditions by waiting
 * for auth initialization to complete.
 */
export function useAuthGuard(): AuthGuardState {
  const { user, isLoading: authLoading, error: authError } = useAuthStore();
  const [localState, setLocalState] = useState<{
    isReady: boolean;
    isLoading: boolean;
    error: string | null;
  }>({
    isReady: false,
    isLoading: true,
    error: null
  });

  // Check auth readiness with validation
  const checkAuthReadiness = useCallback(() => {
    try {
      // Auth must be initialized AND user must have valid ID
      if (!isAuthReady()) {
        setLocalState({
          isReady: false,
          isLoading: authLoading,
          error: null
        });
        return;
      }

      // Additional validation: user ID must not be undefined or invalid
      const userId = user?.id;
      if (!userId || userId === 'undefined' || typeof userId !== 'string') {
        console.warn('useAuthGuard: Invalid user ID detected:', userId);
        setLocalState({
          isReady: false,
          isLoading: false,
          error: 'Invalid user authentication state'
        });
        return;
      }

      // Auth is ready and valid
      setLocalState({
        isReady: true,
        isLoading: false,
        error: null
      });

    } catch (error) {
      console.error('Error in useAuthGuard:', error);
      setLocalState({
        isReady: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Auth guard error'
      });
    }
  }, [user?.id, authLoading, authError]);

  // Monitor auth state changes
  useEffect(() => {
    checkAuthReadiness();
  }, [checkAuthReadiness]);

  // Return safe user object or null
  const safeUser = localState.isReady && user?.id ? { id: user.id } : null;

  return {
    user: safeUser,
    isReady: localState.isReady,
    isLoading: localState.isLoading,
    error: localState.error || authError
  };
}

/**
 * Higher-order component that wraps components requiring authenticated state
 */
export function withAuthGuard<T extends object>(
  Component: React.ComponentType<T>
): React.ComponentType<T> {
  return function AuthGuardedComponent(props: T) {
    const { isReady, isLoading, error } = useAuthGuard();

    if (isLoading) {
      return <div>Loading authentication...</div>;
    }

    if (error) {
      return <div>Authentication error: {error}</div>;
    }

    if (!isReady) {
      return <div>Waiting for authentication...</div>;
    }

    return <Component {...props} />;
  };
}

/**
 * Custom hook for safely accessing user ID in data operations
 * Returns null if auth is not ready, preventing undefined user_id calls
 */
export function useSafeUserId(): string | null {
  const { user, isReady } = useAuthGuard();
  
  // Only return user ID if auth is fully ready and validated
  return isReady && user?.id ? user.id : null;
}